import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, X, Trash2, ListPlus, AlertTriangle, CheckCircle2 } from "lucide-react";
import Modal from "../components/Modal";
import { mastersApi, vendorsApi, apiErrorMessage, type ApiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull } from "@/lib/data";
import {
  CHARGE_UNITS,
  MARGIN_WARN_PCT,
  type ChargeUnit,
  lineQuantity,
  packagePrice,
  toUnit,
  unitSuffix,
} from "@/lib/pricing";
import type { PackagePayload, ServiceOption, VendorOption } from "./types";

interface PackageModalProps {
  isOpen: boolean;
  /** Package being edited; omit for a new one */
  pkg?: PackagePayload | null;
  onClose: () => void;
  onSaved: () => void;
}

interface LineState {
  key: string;
  service_id: number | null;
  name: string;
  category?: string | null;
  description?: string | null;
  unit: ChargeUnit;
  quantity: number;
  unit_price: string;
  cost_price: string;
  vendor_id: number | null;
  is_optional: boolean;
  service_active?: boolean | null;
  notes?: string | null;
}

type DiscountMode = "none" | "percent" | "fixed";

let keySeq = 0;
const newKey = () => `line-${++keySeq}`;
const num = (v: unknown) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export default function PackageModal({ isOpen, pkg, onClose, onSaved }: PackageModalProps) {
  const { showToast } = useToast();
  const isEdit = !!pkg?.id;

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [eventTypeOptions, setEventTypeOptions] = useState<string[]>([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [minGuests, setMinGuests] = useState("");
  const [maxGuests, setMaxGuests] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [lines, setLines] = useState<LineState[]>([]);
  const [inclusions, setInclusions] = useState<string[]>([]);
  const [discountMode, setDiscountMode] = useState<DiscountMode>("none");
  const [discountValue, setDiscountValue] = useState("");
  const [previewGuests, setPreviewGuests] = useState(200);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const pickerInputRef = useRef<HTMLInputElement>(null);

  // Lists the form needs (all services, so lines of archived services still show their names)
  useEffect(() => {
    if (!isOpen) return;
    Promise.all([mastersApi.services.list(), vendorsApi.list("for_select=1"), mastersApi.eventTypes.list()])
      .then(([sRes, vRes, tRes]) => {
        setServices(sRes?.data?.data ?? sRes?.data ?? []);
        setVendors(vRes?.data?.data ?? vRes?.data ?? []);
        const types = (tRes?.data?.data ?? tRes?.data ?? []) as { name: string; status?: string }[];
        setEventTypeOptions(types.filter((t) => !t.status || t.status === "active").map((t) => t.name));
      })
      .catch((err) => showToast(apiErrorMessage(err, "Couldn't load services"), "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fill the form when it opens
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setPickerOpen(false);
    setPickerSearch("");
    setName(pkg?.name ?? "");
    setDescription(pkg?.description ?? "");
    setIsActive(pkg ? pkg.is_active : true);
    setMinGuests(pkg?.min_guests ? String(pkg.min_guests) : "");
    setMaxGuests(pkg?.max_guests ? String(pkg.max_guests) : "");
    setEventTypes(pkg?.event_types ?? []);
    setInclusions(pkg?.inclusions?.length ? pkg.inclusions : []);
    setDiscountMode(pkg?.discount_type ?? "none");
    setDiscountValue(pkg?.discount_type ? String(pkg.discount_value ?? "") : "");
    setPreviewGuests(pkg?.min_guests || 200);
    setLines(
      (pkg?.services_data ?? []).map((l) => ({
        key: newKey(),
        service_id: l.service_id,
        name: l.name,
        category: l.category,
        description: l.description,
        unit: toUnit(l.unit),
        quantity: Math.max(1, Number(l.quantity) || 1),
        unit_price: String(Number(l.unit_price) || 0),
        cost_price: String(Number(l.cost_price) || 0),
        vendor_id: l.vendor_id ?? null,
        is_optional: !!l.is_optional,
        service_active: l.service_active,
        notes: l.notes,
      }))
    );
  }, [isOpen, pkg]);

  const discountType = discountMode === "none" ? null : discountMode;
  const priced = useMemo(
    () => lines.map((l) => ({ unit: l.unit, quantity: l.quantity, unit_price: num(l.unit_price), cost_price: num(l.cost_price), is_optional: l.is_optional })),
    [lines]
  );
  const summary = packagePrice(priced, previewGuests, discountType, discountValue);
  const perGuestCount = lines.filter((l) => l.unit === "guest" && !l.is_optional).length;
  const addOns = lines.filter((l) => l.is_optional);
  const sliderMax = Math.max(1000, num(maxGuests), previewGuests);

  const activeServices = useMemo(() => services.filter((s) => s.is_active !== false), [services]);
  const pickerGroups = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const matches = activeServices.filter(
      (s) => !q || s.name.toLowerCase().includes(q) || (s.category || "").toLowerCase().includes(q)
    );
    const groups = new Map<string, ServiceOption[]>();
    for (const s of matches) {
      const cat = s.category || "Other";
      groups.set(cat, [...(groups.get(cat) || []), s]);
    }
    return [...groups.entries()].sort(([a], [b]) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)));
  }, [activeServices, pickerSearch]);

  const addService = (s: ServiceOption) => {
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        service_id: s.id,
        name: s.name,
        category: s.category,
        unit: toUnit(s.unit),
        quantity: 1,
        unit_price: String(Number(s.selling_price) || 0),
        cost_price: String(Number(s.base_price) || 0),
        vendor_id: s.vendor_id ?? null,
        is_optional: false,
        service_active: true,
      },
    ]);
    setErrors((e) => ({ ...e, lines: "" }));
    setPickerOpen(false);
    setPickerSearch("");
  };

  const updateLine = (key: string, patch: Partial<LineState>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key: string) => setLines((prev) => prev.filter((l) => l.key !== key));

  const toggleEventType = (t: string) =>
    setEventTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const fillInclusionsFromServices = () => {
    const existing = new Set(inclusions.map((i) => i.trim().toLowerCase()));
    const extra = lines.filter((l) => !l.is_optional && !existing.has(l.name.toLowerCase())).map((l) => l.name);
    setInclusions((prev) => [...prev.filter((i) => i.trim()), ...extra]);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = "Give the package a name";
    if (!lines.some((l) => !l.is_optional)) next.lines = "Add at least one included service";
    if (minGuests && maxGuests && num(maxGuests) < num(minGuests)) next.guests = "The maximum must be at least the minimum";
    if (discountMode === "percent" && num(discountValue) > 100) next.discount = "A percentage discount can't be over 100%";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (saving || !validate()) return;
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      is_active: isActive,
      min_guests: minGuests ? Math.round(num(minGuests)) : null,
      max_guests: maxGuests ? Math.round(num(maxGuests)) : null,
      event_types: eventTypes,
      discount_type: discountType,
      discount_value: discountType ? num(discountValue) : 0,
      inclusions: inclusions.map((i) => i.trim()).filter(Boolean),
      services_data: lines.map((l) => ({
        service_id: l.service_id,
        vendor_id: l.vendor_id,
        description: l.description ?? "",
        quantity: l.quantity,
        unit: l.unit,
        unit_price: num(l.unit_price),
        cost_price: num(l.cost_price),
        is_optional: l.is_optional,
        notes: l.notes ?? "",
      })),
    };
    try {
      const res = isEdit ? await mastersApi.packages.update(pkg!.id, payload) : await mastersApi.packages.create(payload);
      showToast(res?.message || (isEdit ? "Package updated" : "Package created"), "success");
      onSaved();
      onClose();
    } catch (err) {
      const fieldErrors: Record<string, string> = {};
      Object.entries((err as ApiError).errors || {}).forEach(([field, msgs]) => {
        const msg = Array.isArray(msgs) ? msgs[0] : String(msgs);
        if (field.startsWith("services_data")) fieldErrors.lines = msg;
        else if (field === "min_guests" || field === "max_guests") fieldErrors.guests = msg;
        else if (field.startsWith("discount")) fieldErrors.discount = msg;
        else fieldErrors[field] = msg;
      });
      setErrors(fieldErrors);
      showToast(apiErrorMessage(err, "Couldn't save the package"), "error");
    } finally {
      setSaving(false);
    }
  };

  const label = "block text-xs font-semibold text-stone-500 mb-1.5";
  const input = "w-full h-11 px-3 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";
  const small = "h-10 px-2.5 rounded-lg border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";
  const sectionTitle = "flex flex-wrap items-baseline justify-between gap-2 mb-3";
  const err = (key: string) => (errors[key] ? <p className="text-xs text-red-600 mt-1">{errors[key]}</p> : null);

  const marginTone =
    summary.level === "loss" ? "bg-red-50 text-red-700" : summary.level === "low" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700";
  const barColour = summary.level === "loss" ? "bg-red-500" : summary.level === "low" ? "bg-amber-500" : "bg-emerald-500";

  const footer = (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Compact price line for phones, where the price panel is below the form */}
      <div className="lg:hidden text-sm text-stone-600 flex-1">
        <span className="font-bold text-stone-800 tabular-nums">{formatCurrencyFull(summary.price)}</span> for {previewGuests} guests
        {summary.price > 0 && <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${marginTone}`}>{summary.pct.toFixed(0)}% margin</span>}
      </div>
      <div className="flex gap-2 sm:ml-auto">
        <button type="button" onClick={onClose} className="flex-1 sm:flex-none h-11 px-5 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="flex-1 sm:flex-none h-11 px-6 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center min-w-[140px]">
          {saving ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : isEdit ? "Save package" : "Create package"}
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? `Edit package` : "New package"} size="xl" footer={footer}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        {/* ── Form ───────────────────────────────────────── */}
        <div className="space-y-7 min-w-0">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="pkg-name" className={label}>Package name</label>
              <input id="pkg-name" value={name} onChange={(e) => { setName(e.target.value); setErrors((x) => ({ ...x, name: "" })); }} placeholder="e.g. Silver Wedding" className={input} />
              {err("name")}
            </div>
            <div>
              <label htmlFor="pkg-desc" className={label}>Short description <span className="font-normal">(optional)</span></label>
              <input id="pkg-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Our most booked wedding package" className={input} />
            </div>
          </div>

          {/* Fits */}
          <section>
            <div className={sectionTitle}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700">Fits</h3>
              <span className="text-xs text-stone-400">Helps staff pick the right package for an event</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <span className={label}>Guests <span className="font-normal">(optional)</span></span>
                <div className="flex items-center gap-2">
                  <input aria-label="Minimum guests" inputMode="numeric" value={minGuests} onChange={(e) => setMinGuests(e.target.value.replace(/\D/g, ""))} placeholder="Min" className={`${input} tabular-nums`} />
                  <span className="text-stone-400 text-sm">to</span>
                  <input aria-label="Maximum guests" inputMode="numeric" value={maxGuests} onChange={(e) => setMaxGuests(e.target.value.replace(/\D/g, ""))} placeholder="Max" className={`${input} tabular-nums`} />
                </div>
                {err("guests")}
              </div>
              <div>
                <span className={label}>Event types <span className="font-normal">(none = any)</span></span>
                <div className="flex flex-wrap gap-2">
                  {eventTypeOptions.length === 0 && <span className="text-xs text-stone-400">Add event types under Admin → Event Types.</span>}
                  {[...new Set([...eventTypeOptions, ...eventTypes])].map((t) => (
                    <button key={t} type="button" aria-pressed={eventTypes.includes(t)} onClick={() => toggleEventType(t)}
                      className={`min-h-10 px-3.5 rounded-full border text-sm transition-colors ${eventTypes.includes(t) ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Services */}
          <section>
            <div className={sectionTitle}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700">Services</h3>
              <span className="text-xs text-stone-400">
                {lines.length} service{lines.length === 1 ? "" : "s"}{perGuestCount ? ` · ${perGuestCount} priced per guest` : ""}{addOns.length ? ` · ${addOns.length} add-on${addOns.length === 1 ? "" : "s"}` : ""}
              </span>
            </div>

            {lines.length > 0 && (
              <div className="rounded-xl border border-stone-200 divide-y divide-stone-100">
                {lines.map((l) => {
                  const qty = lineQuantity({ unit: l.unit, quantity: l.quantity }, previewGuests);
                  const amount = qty * num(l.unit_price);
                  return (
                    <div key={l.key} className={`p-3 ${l.is_optional ? "bg-amber-50/40" : ""}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-stone-800 break-words">
                            {l.name}
                            {l.service_active === false && <span className="ml-2 px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 text-[10px] font-bold uppercase">Archived service</span>}
                          </p>
                          <p className="text-xs text-stone-500">{l.category || "No category"}</p>
                        </div>
                        <div className="flex items-start gap-1 shrink-0">
                          <div className="text-right">
                            <p className={`text-sm font-bold tabular-nums ${l.is_optional ? "text-stone-400" : "text-stone-800"}`}>{formatCurrencyFull(amount)}</p>
                            <p className="text-[11px] text-stone-400 tabular-nums">cost {formatCurrencyFull(qty * num(l.cost_price))}</p>
                          </div>
                          <button type="button" onClick={() => removeLine(l.key)} aria-label={`Remove ${l.name}`} className="w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-[130px_110px_1fr_1fr] gap-2 items-end">
                        <div>
                          <span className="block text-[11px] text-stone-400 mb-1">Charged</span>
                          <select aria-label={`How ${l.name} is charged`} value={l.unit} onChange={(e) => updateLine(l.key, { unit: toUnit(e.target.value) })} className={`${small} w-full`}>
                            {CHARGE_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <span className="block text-[11px] text-stone-400 mb-1">Quantity</span>
                          {l.unit === "guest" ? (
                            <span className="h-10 px-3 inline-flex items-center rounded-lg bg-amber-50 text-amber-800 text-xs font-semibold whitespace-nowrap">× guests</span>
                          ) : (
                            <input aria-label={`Quantity of ${l.name}`} type="number" min={1} value={l.quantity}
                              onChange={(e) => updateLine(l.key, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                              className={`${small} w-full tabular-nums`} />
                          )}
                        </div>
                        <div>
                          <span className="block text-[11px] text-stone-400 mb-1">Price {unitSuffix(l.unit)}</span>
                          <input aria-label={`Price of ${l.name}`} inputMode="decimal" value={l.unit_price} onChange={(e) => updateLine(l.key, { unit_price: e.target.value.replace(/[^0-9.]/g, "") })} className={`${small} w-full tabular-nums`} />
                        </div>
                        <div>
                          <span className="block text-[11px] text-stone-400 mb-1">Cost {unitSuffix(l.unit)}</span>
                          <input aria-label={`Cost of ${l.name}`} inputMode="decimal" value={l.cost_price} onChange={(e) => updateLine(l.key, { cost_price: e.target.value.replace(/[^0-9.]/g, "") })}
                            className={`${small} w-full tabular-nums ${num(l.cost_price) === 0 ? "border-amber-300 bg-amber-50/50" : ""}`} />
                        </div>
                        <div className="col-span-2 sm:col-span-3">
                          <span className="block text-[11px] text-stone-400 mb-1">Vendor</span>
                          <select aria-label={`Vendor for ${l.name}`} value={l.vendor_id ?? ""} onChange={(e) => updateLine(l.key, { vendor_id: e.target.value ? Number(e.target.value) : null })} className={`${small} w-full`}>
                            <option value="">In-house (no vendor)</option>
                            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                          </select>
                        </div>
                        <label className="col-span-2 sm:col-span-1 h-10 inline-flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                          <input type="checkbox" checked={l.is_optional} onChange={(e) => updateLine(l.key, { is_optional: e.target.checked })} className="w-4 h-4 accent-amber-600" />
                          Optional add-on
                        </label>
                      </div>
                      {num(l.cost_price) === 0 && !l.is_optional && (
                        <p className="mt-1.5 text-[11px] text-amber-700">No cost entered, so the margin will look higher than it is.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Service picker */}
            {pickerOpen ? (
              <div className="mt-3 rounded-xl border border-amber-300 bg-white p-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input ref={pickerInputRef} autoFocus value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Escape") setPickerOpen(false); }}
                    placeholder="Search services by name or category…" aria-label="Search services" className={`${input} pl-9 pr-10`} />
                  <button type="button" onClick={() => setPickerOpen(false)} aria-label="Close service search" className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-lg flex items-center justify-center text-stone-400 hover:bg-stone-100">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-2 max-h-64 overflow-y-auto">
                  {pickerGroups.length === 0 && <p className="px-2 py-4 text-sm text-stone-500 text-center">No active services match. Add services on the Services page.</p>}
                  {pickerGroups.map(([cat, list]) => (
                    <div key={cat} className="py-1">
                      <p className="px-2 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-stone-400">{cat}</p>
                      {list.map((s) => {
                        const already = lines.some((l) => l.service_id === s.id);
                        return (
                          <button key={s.id} type="button" onClick={() => addService(s)} className="w-full min-h-10 px-2 rounded-lg flex items-center justify-between gap-3 text-left hover:bg-amber-50">
                            <span className="text-sm text-stone-800 min-w-0 truncate">{s.name}{already && <span className="ml-2 text-[11px] text-stone-400">already added</span>}</span>
                            <span className="text-xs text-stone-500 tabular-nums shrink-0">{formatCurrencyFull(s.selling_price)} {unitSuffix(s.unit)}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setPickerOpen(true)} className="mt-3 h-10 inline-flex items-center gap-2 px-4 rounded-xl border border-dashed border-stone-300 text-sm font-semibold text-stone-700 hover:border-amber-400 hover:bg-amber-50">
                <Plus className="w-4 h-4" /> Add service
              </button>
            )}
            {err("lines")}
          </section>

          {/* What's included */}
          <section>
            <div className={sectionTitle}>
              <h3 className="text-xs font-bold uppercase tracking-wide text-stone-700">What's included</h3>
              <span className="text-xs text-stone-400">The promise shown to the client</span>
            </div>
            <div className="space-y-2">
              {inclusions.map((inc, i) => (
                <div key={i} className="flex gap-2">
                  <input aria-label={`Included item ${i + 1}`} value={inc} onChange={(e) => setInclusions((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} placeholder="e.g. Hall and lawn for 8 hours" className={input} />
                  <button type="button" onClick={() => setInclusions((prev) => prev.filter((_, j) => j !== i))} aria-label={`Remove included item ${i + 1}`} className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setInclusions((prev) => [...prev, ""])} className="h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border border-stone-200 text-sm text-stone-700 hover:bg-stone-50">
                  <Plus className="w-4 h-4" /> Add a line
                </button>
                {lines.some((l) => !l.is_optional) && (
                  <button type="button" onClick={fillInclusionsFromServices} className="h-10 inline-flex items-center gap-1.5 px-3 rounded-xl border border-stone-200 text-sm text-stone-700 hover:bg-stone-50">
                    <ListPlus className="w-4 h-4" /> One line per service
                  </button>
                )}
              </div>
            </div>
          </section>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-5 h-5 accent-emerald-600" />
            <span className="text-sm text-stone-700"><b>Active</b>: offered on new quotations and events</span>
          </label>
        </div>

        {/* ── Price panel ────────────────────────────────── */}
        <aside className="space-y-4 lg:sticky lg:top-0 lg:self-start" aria-label="Price">
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-4 space-y-3">
            <div>
              <label htmlFor="pkg-preview" className="text-xs font-semibold text-stone-500">
                Price it for <span className="text-stone-800 text-sm font-bold tabular-nums">{previewGuests}</span> guests
              </label>
              <input id="pkg-preview" type="range" min={10} max={sliderMax} step={10} value={previewGuests} onChange={(e) => setPreviewGuests(Number(e.target.value))} className="w-full accent-amber-600 h-8" />
            </div>
            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-sm tabular-nums">
              <dt className="text-stone-500">Services total</dt><dd className="text-right">{formatCurrencyFull(summary.list)}</dd>
              <dt className="text-stone-500">Package discount</dt><dd className="text-right text-emerald-700">{summary.discount > 0 ? `−${formatCurrencyFull(summary.discount)}` : "—"}</dd>
              <dt className="text-stone-700 font-semibold">Client pays</dt><dd className="text-right text-lg font-bold text-stone-900">{formatCurrencyFull(summary.price)}</dd>
              <dt className="text-stone-500">Per guest</dt><dd className="text-right">{formatCurrencyFull(previewGuests ? summary.price / previewGuests : 0)}</dd>
              <dt className="text-stone-500">Our cost</dt><dd className="text-right">{formatCurrencyFull(summary.cost)}</dd>
              <dt className="text-stone-500">Profit</dt><dd className="text-right font-semibold">{formatCurrencyFull(summary.amount)}</dd>
            </dl>
            <div className="h-2 rounded-full bg-stone-200 overflow-hidden" aria-hidden="true">
              <div className={`h-full rounded-full transition-all ${barColour}`} style={{ width: `${Math.max(0, Math.min(100, summary.pct))}%` }} />
            </div>
            {summary.price > 0 && (
              <p className={`text-xs rounded-lg px-2.5 py-2 flex gap-1.5 ${marginTone}`} role="status">
                {summary.level === "good" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                {summary.level === "loss"
                  ? `Loss of ${formatCurrencyFull(-summary.amount)} at ${previewGuests} guests. Raise prices or reduce the discount.`
                  : summary.level === "low"
                    ? `Margin ${summary.pct.toFixed(1)}% is below ${MARGIN_WARN_PCT}%. Check costs and the discount.`
                    : `Margin ${summary.pct.toFixed(1)}% at ${previewGuests} guests.`}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-stone-200 p-4 space-y-2">
            <span className="text-xs font-semibold text-stone-500">Package discount</span>
            <div className="flex gap-1.5" role="group" aria-label="Discount type">
              {(["none", "percent", "fixed"] as DiscountMode[]).map((m) => (
                <button key={m} type="button" aria-pressed={discountMode === m} onClick={() => setDiscountMode(m)}
                  className={`flex-1 h-10 rounded-lg border text-sm ${discountMode === m ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                  {m === "none" ? "None" : m === "percent" ? "% off" : "₹ off"}
                </button>
              ))}
            </div>
            {discountMode !== "none" && (
              <input aria-label="Discount value" inputMode="decimal" value={discountValue} onChange={(e) => setDiscountValue(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder={discountMode === "percent" ? "e.g. 5" : "e.g. 25000"} className={`${input} tabular-nums`} />
            )}
            {err("discount")}
            <p className="text-[11px] text-stone-400">Applied as the quotation's discount, so the invoice stays itemised.</p>
          </div>

          <div className="rounded-xl border border-stone-200 p-4">
            <span className="text-xs font-semibold text-stone-500">Add-ons offered</span>
            {addOns.length === 0 ? (
              <p className="text-xs text-stone-400 mt-1">None. Tick “Optional add-on” on a service to offer it separately.</p>
            ) : (
              <ul className="mt-1.5 space-y-1 text-sm">
                {addOns.map((l) => (
                  <li key={l.key} className="flex justify-between gap-2">
                    <span className="text-stone-700 truncate">{l.name}</span>
                    <span className="text-stone-500 tabular-nums shrink-0">{formatCurrencyFull(lineQuantity({ unit: l.unit, quantity: l.quantity }, previewGuests) * num(l.unit_price))}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </Modal>
  );
}
